import { groups } from '@lafayette/content';
import type { GameProjection } from '@lafayette/protocol';
import { GroupChip } from '@lafayette/ui';
import { useMemo, type CSSProperties } from 'react';

import { useReducedMotion } from '../useReducedMotion.js';
import styles from './GameOver.module.css';
import { nameOf } from './selectors.js';

const PIECES = 90;

interface Piece {
  style: CSSProperties;
}

/**
 * Confetti in the property colors. Each piece gets its own lane, delay and spin
 * once, so a re-render never restarts the burst.
 */
function useConfetti(seed: string): Piece[] {
  return useMemo(() => {
    // A tiny deterministic generator, so the burst is stable across renders.
    let state = [...seed].reduce((h, c) => (Math.imul(h, 31) + c.charCodeAt(0)) >>> 0, 7);
    const next = (): number => {
      state = (Math.imul(state, 1103515245) + 12345) >>> 0;
      return state / 4294967296;
    };

    return Array.from({ length: PIECES }, () => {
      const color = groups[Math.floor(next() * groups.length)]!.color;
      return {
        style: {
          left: `${next() * 100}%`,
          background: color,
          animationDelay: `${next() * 2.2}s`,
          animationDuration: `${2.6 + next() * 2.2}s`,
          width: `${5 + next() * 7}px`,
          height: `${9 + next() * 9}px`,
          ['--drift' as string]: `${(next() - 0.5) * 30}vw`,
          ['--spin' as string]: `${540 + next() * 720}deg`,
        },
      };
    });
  }, [seed]);
}

export interface GameOverProps {
  projection: GameProjection;
  /** True once the player has waved the celebration away. */
  dismissed: boolean;
  onDismiss: () => void;
}

/** The end of the match: a burst for the winner, a banner for everyone. */
export function GameOver({ projection, dismissed, onDismiss }: GameOverProps) {
  const reducedMotion = useReducedMotion();
  const winnerId = projection.winnerId;
  const confetti = useConfetti(winnerId ?? '');

  if (!winnerId) return null;

  const won = winnerId === projection.self.id;
  const winner = won ? projection.self : projection.opponents.find((o) => o.id === winnerId);
  const malls = winner?.completeMalls ?? [];

  return (
    <>
      {won && !dismissed && !reducedMotion ? (
        <div className={styles.confetti} data-testid="confetti" aria-hidden="true">
          {confetti.map((piece, index) => (
            <span key={index} className={styles.piece} style={piece.style} />
          ))}
        </div>
      ) : null}

      {dismissed ? null : (
        <div className={styles.banner} role="status" data-testid="game-over">
          <h2 className={`${styles.headline} ${won ? styles.headlineWon : ''}`}>
            {won ? 'You win!' : `${nameOf(projection, winnerId)} wins`}
          </h2>
          <p className={styles.detail}>
            {won
              ? 'Three strip malls, fully leased.'
              : 'Three strip malls, fully leased. Better luck next time.'}
          </p>
          <div className={styles.malls} aria-label="The winning strip malls">
            {malls.map((group) => (
              <GroupChip key={group} groups={group} size="large" />
            ))}
          </div>
          <button
            type="button"
            className="lf-button"
            data-testid="dismiss-game-over"
            onClick={onDismiss}
          >
            Look at the table
          </button>
        </div>
      )}
    </>
  );
}
